import React, { useCallback, useEffect, useState } from "react";
import { pedidoBackend } from "../../registro/registro";
import Spinner from "../../components/spinner/Spinner";
import {
  Aviso,
  Badge,
  Boton,
  Filtros,
  Paginacion,
  Panel,
  Tabla,
  TablaWrapper,
  Vacio,
} from "./panelStyles";

// Bitácora completa: logins (incluidos los fallidos), ventas, consultas,
// errores de la web y actividad del panel. No se borra nunca.

const ACCIONES = [
  { valor: "", texto: "Todas las acciones" },
  { valor: "web.login", texto: "Ingresos" },
  { valor: "web.login-fallido", texto: "Logins fallidos" },
  { valor: "venta.snapshot", texto: "Lo que eligió el vendedor" },
  { valor: "venta.registrar", texto: "Ventas cargadas" },
  { valor: "venta.resultado", texto: "Resultados de venta" },
  { valor: "aviso.enviar", texto: "Avisos de asignación" },
  { valor: "cliente.consulta", texto: "Consultas de cliente" },
  { valor: "web.error", texto: "Errores de la web" },
  { valor: "web.logout", texto: "Cierres de sesión" },
  { valor: "web.sesion-vencida", texto: "Sesiones vencidas" },
  { valor: "acceso.identidad", texto: "Accesos rechazados" },
  { valor: "reporte.enviar", texto: "Reportes enviados" },
  { valor: "reporte.descargar", texto: "Reportes descargados" },
];

const fecha = (iso) =>
  new Date(iso).toLocaleString("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const BitacoraPanel = () => {
  const [filtros, setFiltros] = useState({ usuario: "", nivel: "", accion: "" });
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [paging, setPaging] = useState({ page: 1, totalPages: 1, total: 0 });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const params = { page, pageSize: 100 };
      if (filtros.usuario.trim()) params.usuario = filtros.usuario.trim().toLowerCase();
      if (filtros.nivel) params.nivel = filtros.nivel;
      if (filtros.accion) params.accion = filtros.accion;
      const { data } = await pedidoBackend("get", "/panel/auditoria", { params });
      setItems(data.data || []);
      setPaging(data.paging || { page: 1, totalPages: 1, total: 0 });
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "No se pudo consultar la bitácora");
      setItems([]);
    } finally {
      setCargando(false);
    }
  }, [filtros, page]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiar = (campo, valor) => {
    setPage(1);
    setFiltros((f) => ({ ...f, [campo]: valor }));
  };

  return (
    <>
      <Panel>
        <Filtros>
          <label>
            Usuario
            <input
              type="search"
              placeholder="usuario de login"
              value={filtros.usuario}
              onChange={(e) => cambiar("usuario", e.target.value)}
            />
          </label>
          <label>
            Tipo
            <select value={filtros.nivel} onChange={(e) => cambiar("nivel", e.target.value)}>
              <option value="">Todos</option>
              <option value="AUDITORIA">Actividad</option>
              <option value="SEGURIDAD">Seguridad</option>
              <option value="ERROR">Errores</option>
            </select>
          </label>
          <label>
            Acción
            <select value={filtros.accion} onChange={(e) => cambiar("accion", e.target.value)}>
              {ACCIONES.map((a) => (
                <option key={a.valor || "todas"} value={a.valor}>
                  {a.texto}
                </option>
              ))}
            </select>
          </label>
          <Boton type="button" $secundario onClick={cargar} disabled={cargando}>
            Actualizar
          </Boton>
        </Filtros>
        <Aviso>
          El registro se conserva sin límite de tiempo: sirve para ver quién no acierta la
          contraseña, por qué falló una venta o quién consultó el panel.
        </Aviso>
        {error && <Aviso $error>{error}</Aviso>}
      </Panel>

      <TablaWrapper>
        <Tabla $min="820px">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Tipo</th>
              <th>Acción</th>
              <th>Usuario</th>
              <th>Qué pasó</th>
              <th>Detalle</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {cargando && (
              <tr>
                <Vacio colSpan={7}>
                  <Spinner />
                </Vacio>
              </tr>
            )}
            {!cargando && items.length === 0 && (
              <tr>
                <Vacio colSpan={7}>No hay registros con estos filtros.</Vacio>
              </tr>
            )}
            {!cargando &&
              items.map((e) => (
                <tr key={e.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{fecha(e.fecha)}</td>
                  <td>
                    <Badge $estado={e.nivel}>{e.nivel}</Badge>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>{e.accion}</td>
                  <td>{e.usuario || "—"}</td>
                  <td style={{ maxWidth: 300, overflowWrap: "anywhere", wordBreak: "break-word" }}>
                    {e.mensaje}
                  </td>
                  <td>
                    <div
                      style={{
                        maxWidth: 340,
                        maxHeight: 110,
                        overflow: "auto",
                        fontSize: "0.75rem",
                        color: "var(--grisMedio)",
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {e.detalle || "—"}
                    </div>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>{e.ip || "—"}</td>
                </tr>
              ))}
          </tbody>
        </Tabla>
        <Paginacion>
          <span>
            {paging.total} registros · página {paging.page} de {paging.totalPages}
          </span>
          <Boton type="button" $secundario disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ← Anterior
          </Boton>
          <Boton
            type="button"
            $secundario
            disabled={page >= paging.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente →
          </Boton>
        </Paginacion>
      </TablaWrapper>
    </>
  );
};

export default BitacoraPanel;
